import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ContactInteractionModel, ContactModel } from '../models/contact.model';
import {
  CONTACTS_SERVICE,
  IContactsService,
} from '../services/IContact.service';
import * as fromActions from './contact.actions';
import * as fromReducer from './contact.reducer';
import { ContactStore } from './contact.store';

@Injectable()
export class ContactsEffects {
  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.fetchContactBeginAction),
      switchMap(() =>
        this.service.loadRemoteContacts().pipe(
          map((contacList: ContactModel[]) =>
            fromActions.fetchContactSuccessAction({ contacList })
          ),
          catchError((error) => {
            console.error('Could not fetch contacts');
            return of(fromActions.fetchContactFailAction({ errors: error }));
          })
        )
      )
    )
  );

  loadInteractions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.fetchInteractionsBeginAction),
      switchMap((action) =>
        this.service.loadContactInteractions(action.contactId).pipe(
          map((interactions: ContactInteractionModel[]) =>
            fromActions.fetchInteractionsSuccessAction({ interactions })
          ),
          catchError((error) => {
            console.error('Could not fetch contact interactions');
            return of(
              fromActions.fetchInteractionsFailAction({ errors: error })
            );
          })
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.createContactBeginAction),
      withLatestFrom(this.store.contacts.data$),
      switchMap(([action, currentContacts]) =>
        this.service.createContact(action.contactForm).pipe(
          map((contact: ContactModel) => {
            const contacts = [contact, ...currentContacts];
            return fromActions.createContactSuccessAction({ contacts });
          }),
          catchError((errors) => {
            console.error('Could not Create contact');
            return of(fromActions.createContactFailAction({ errors }));
          })
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.deleteContactBeginAction),
      withLatestFrom(this.store.contacts.data$),
      switchMap(([action, currentContacts]) =>
        this.service.delete(action.contactId).pipe(
          map((contactId) => {
            const contactList = [
              ...currentContacts.filter(
                (c: ContactModel) => c.id !== action.contactId
              ),
            ];
            return fromActions.deleteContactSuccessAction({ contactList });
          }),
          catchError((errors) => {
            console.error('Could not Delete contact');
            return of(fromActions.deleteContactFailAction({ errors }));
          })
        )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.updateContactBeginAction),
      withLatestFrom(this.store.contacts.data$),
      switchMap(([action, currentContacts]) =>
        this.service.update(action.contactForm).pipe(
          map((updatedContact) => {
            const contactList = [...currentContacts];

            const idx = currentContacts.findIndex(
              (m) => m.id === updatedContact.id
            );

            if (idx !== -1) {
              contactList.splice(idx, 1, updatedContact);
            }

            return fromActions.updateContactSuccessAction({
              contactList,
            });
          }),
          catchError((errors) => {
            console.error('Could not Update contact');
            return of(fromActions.updateContactFailAction({ errors }));
          })
        )
      )
    )
  );

  import$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.importContactBeginAction),
      withLatestFrom(this.store.contacts.data$),
      switchMap(([action, currentContacts]) =>
        this.service.import(action.contactList).pipe(
          map((newContactList) => {
            const contactList = [...newContactList, ...currentContacts];
            return fromActions.importContactSuccessAction({ contactList });
          }),
          catchError((errors) =>
            of(fromActions.importContactFailAction({ errors }))
          )
        )
      )
    )
  );

  filter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.filterContactsBeginAction),
      withLatestFrom(this.store.contacts.data$),
      switchMap(([action, storedContacts]) => {
        const contactList =
          (action as any).clientType === 'ALL'
            ? storedContacts
            : storedContacts.filter(
                (item) => item.type === (action as any).clientType
              );

        return of(fromActions.filterContactsSuccessAction({ contactList }));
      }),
      catchError((errors) => {
        console.error('**** Filter contacts Error');
        return of(fromActions.filterContactsFailAction({ errors }));
      })
    )
  );

  createInteraction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.createInteractionBeginAction),
      withLatestFrom(this.store.interactions.data$),
      switchMap(([action, currentInteractions]) =>
        this.service.createInteraction(action.contactId, action.config).pipe(
          map((response) => {
            const interactions = [response, ...currentInteractions];
            return fromActions.createInteractionSuccessAction({ interactions });
          }),
          catchError((errors) =>
            of(fromActions.createInteractionFailAction({ errors }))
          )
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: ContactStore,
    @Inject(CONTACTS_SERVICE)
    private service: IContactsService<any, ContactModel>
  ) {}
}
